import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Motion";
import NatalResults from "@/components/NatalResults";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";

const LOADING_STEPS = [
  "Localizzo il luogo della tua nascita…",
  "Calcolo le posizioni di Sole, Luna e pianeti…",
  "Traccio Ascendente e case…",
  "ARIES legge i tuoi talenti e doni…",
  "Compongo la tua sessione personalizzata…",
];

const field = "w-full bg-transparent border-b border-[#2A1F14]/15 py-3 text-[#2A1F14] focus:border-gold outline-none transition-colors placeholder:text-[#2A1F14]/30";

function PlaceInput({ value, onChange, onSelect }) {
  const [sugg, setSugg] = useState([]);
  const [focus, setFocus] = useState(false);
  const timer = useRef(null);
  const skip = useRef(false);
  useEffect(() => {
    clearTimeout(timer.current);
    if (skip.current) { skip.current = false; return; }
    if (value.trim().length < 3) { setSugg([]); return; }
    timer.current = setTimeout(() => api.get("/geocode", { params: { q: value } }).then((r) => setSugg(r.data)).catch(() => setSugg([])), 450);
    return () => clearTimeout(timer.current);
  }, [value]);
  return (
    <div className="relative">
      <div className="flex items-center gap-3">
        <MapPin size={16} className="text-gold shrink-0" />
        <input required placeholder="Luogo di nascita (es. Roma, Italia)" value={value} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)}
          onChange={(e) => { onChange(e.target.value); onSelect(null); }} className={field} data-testid="natal-place" autoComplete="off" />
      </div>
      <AnimatePresence>
        {focus && sugg.length > 0 && (
          <motion.ul initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute left-0 right-0 top-full mt-2 z-20 glass rounded-xl overflow-hidden" data-testid="natal-place-suggestions">
            {sugg.map((s, i) => (
              <li key={i}>
                <button type="button" onMouseDown={() => { skip.current = true; onChange(s.label); onSelect(s); setSugg([]); }} data-testid="natal-place-option"
                  className="w-full text-left px-4 py-2.5 text-sm text-[#2A1F14]/80 hover:bg-gold/15 hover:text-[#2A1F14] transition-colors truncate">{s.label}</button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function Loading() {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => Math.min(x + 1, LOADING_STEPS.length - 1)), 4500); return () => clearInterval(t); }, []);
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="natal-loading">
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 14, ease: "linear" }} className="relative w-40 h-40 rounded-full border border-gold/40 flex items-center justify-center mb-10">
        <div className="absolute inset-4 rounded-full border border-[#2A1F14]/10" />
        <div className="absolute inset-10 rounded-full border border-gold/20" />
        <span className="absolute top-2 w-2 h-2 rounded-full bg-gold" />
        <span className="absolute bottom-6 right-6 w-1.5 h-1.5 rounded-full bg-[#2A1F14]/40" />
        <Star size={22} className="text-gold" />
      </motion.div>
      <AnimatePresence mode="wait">
        <motion.p key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="font-display text-2xl text-[#2A1F14]">{LOADING_STEPS[i]}</motion.p>
      </AnimatePresence>
      <p className="text-[#2A1F14]/45 text-sm mt-4">Può richiedere fino a un minuto.</p>
    </div>
  );
}

export default function TemaNatale() {
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", birth_date: "", birth_time: "", time_known: true, birth_place: "" });
  const [placeSel, setPlaceSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  useEffect(() => {
    const id = params.get("id");
    if (!id) return;
    setLoading(true);
    api.get(`/natal-chart/${id}`).then((r) => setResult(r.data)).catch(() => toast.error("Tema non trovato")).finally(() => setLoading(false));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.time_known && !form.birth_time) { toast.error("Inserisci l'ora di nascita oppure indica che non la conosci."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/natal-chart", { ...form, birth_time: form.birth_time || "12:00", lat: placeSel?.lat, lon: placeSel?.lon });
      setResult(data);
      track("natal_chart");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const reset = () => { setResult(null); setParams({}); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="pt-32 pb-28 min-h-screen constellation">
      <Seo title={result?.interpretation?.titolo ? `Tema Natale di ${result.name}: ${result.interpretation.titolo}` : "Tema Natale · Talenti e Doni — Calcolo gratuito"}
        description={result ? `Il tema natale di ${result.name}: Sole in ${result.chart.planets[0].sign}, Luna in ${result.chart.planets[1].sign}. Talenti, doni dell'anima e sessione personalizzata.` : "Calcola gratis il tuo tema natale con precisione astronomica: Sole, Luna, Ascendente, pianeti e case. ARIES rivela i tuoi talenti, i doni dell'anima e propone una sessione personalizzata."}
        noindex={!!result}
        jsonLd={!result ? { "@context": "https://schema.org", "@type": "WebApplication", name: "Tema Natale · Talenti e Doni", applicationCategory: "LifestyleApplication", operatingSystem: "Web", inLanguage: "it", offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" }, provider: { "@type": "Organization", name: "LUX AETERNA" } } : undefined} />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {loading ? <Loading /> : result ? <NatalResults data={result} onReset={reset} /> : (
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-5">Tema Natale · Talenti e Doni</p>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#2A1F14] leading-[1.02] mb-8">Il cielo della tua nascita, letto come una mappa dei tuoi doni.</h1>
              <p className="text-[#2A1F14]/55 text-lg leading-relaxed mb-10 max-w-xl">Inserisci data, ora e luogo di nascita: calcoliamo il tuo tema natale con precisione astronomica e ARIES rivela i talenti che porti, i doni della tua anima e una sessione costruita interamente su di te.</p>
              <div className="space-y-5">
                {[["Precisione astronomica", "Effemeridi svizzere: Sole, Luna, pianeti, Ascendente e case."],
                  ["Rivelazione dei talenti", "Cinque talenti e tre doni dell'anima letti in chiave evolutiva."],
                  ["Sessione su misura", "Un percorso personalizzato con Davide Bellafiore, costruito sul tuo cielo."]].map(([t, d], i) => (
                  <div key={i} className="flex gap-4">
                    <span className="font-display text-gold text-xl w-8 shrink-0">0{i + 1}</span>
                    <div><p className="text-[#2A1F14]">{t}</p><p className="text-[#2A1F14]/50 text-sm">{d}</p></div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <form onSubmit={submit} className="glass rounded-3xl p-8 sm:p-10 space-y-7" data-testid="natal-form">
                <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em]"><Sparkles size={14} /> I tuoi dati di nascita</div>
                <input required placeholder="Il tuo nome" value={form.name} onChange={set("name")} className={field} data-testid="natal-name" maxLength={80} />
                <div>
                  <label className="text-[#2A1F14]/45 text-xs uppercase tracking-wider">Data di nascita</label>
                  <input required type="date" value={form.birth_date} onChange={set("birth_date")} className={field} data-testid="natal-date" min="1900-01-01" max="2030-12-31" />
                </div>
                <div>
                  <label className="text-[#2A1F14]/45 text-xs uppercase tracking-wider">Ora di nascita</label>
                  <input type="time" value={form.birth_time} onChange={set("birth_time")} disabled={!form.time_known} className={field + " disabled:opacity-40"} data-testid="natal-time" />
                  <label className="flex items-center gap-2 mt-3 text-sm text-[#2A1F14]/65 cursor-pointer">
                    <input type="checkbox" checked={!form.time_known} onChange={(e) => setForm((f) => ({ ...f, time_known: !e.target.checked }))} className="accent-[#B9863F]" data-testid="natal-time-unknown" />
                    Non conosco l'ora di nascita
                  </label>
                </div>
                <PlaceInput value={form.birth_place} onChange={(v) => setForm((f) => ({ ...f, birth_place: v }))} onSelect={setPlaceSel} />
                <button type="submit" data-testid="natal-submit" className="w-full py-4 rounded-full bg-gold text-black text-sm font-medium tracking-wide hover:bg-[#D4A056] transition-colors">
                  Rivela i miei talenti
                </button>
                <p className="text-[#2A1F14]/40 text-xs leading-relaxed">I dati servono solo al calcolo del tema. Nessuna previsione: una lettura evolutiva di talenti e potenziale.</p>
              </form>
            </Reveal>
          </div>
        )}
      </div>
    </div>
  );
}
